/**
 * The rules every week generator shares about what a slot wants and what a
 * week may hold — the planner (`generateDinnerOnlyPlan`), Régénérer
 * (`buildGenerationPlan`) and the weekly email (`generateProposedPlan`).
 *
 * They used to be three private copies, and the plan eval
 * (`npm run eval:plans`) showed what that costs: Régénérer never served the
 * vegetarian Wednesday the cook had set, the email served meat to a declared
 * vegetarian and nuts to a nut allergy, and the planner put three or more fish
 * dinners in half its weeks. One module, so the three cannot drift again:
 *
 * - `planSlotFor`   what the cook's schedule asks of a day (kind, time cap);
 * - `WeekBalance`   no more than two dinners on one main protein, and no more
 *                   fish dinners than two (or the fish days the cook set);
 * - `narrowForSlot` applies both, giving way from the loosest rule up so a
 *                   slot is never left empty for the sake of a soft rule.
 *
 * Hard rules — allergies, excluded ingredients, declared diets — are not here:
 * they live in `cookPreferences.ts` (`isExcluded`) and never give way. The same
 * meal twice lives in `mealIdentity.ts`. Pure and server-safe.
 */
import { DEFAULT_MEAL_SCHEDULE, type MealSchedulePreferences } from '../redux/mealSchedule';
import { classifyDietKind, isFishRecipe, isVegetarianRecipe, detectProtein } from '../utils/dietClassifier';
import { dishFormatOf, mainProteinOf } from './mealIdentity';
import { addDays, parseIsoDate, weekStartOf } from '../utils/localDate';

export type SlotKind = 'vegetarian' | 'fish' | 'meat' | 'pasta' | 'any';

/** One dinner to fill, as the cook's schedule describes it. */
export interface PlanSlot {
  date: string;
  /** 0 = Sunday … 6 = Saturday. */
  dayOfWeek: number;
  kind: SlotKind;
  /** Minutes, or null for no limit. */
  maxCookTime: number | null;
  /** A slow-cooker or make-ahead dish may ignore the time cap. */
  allowPrepAhead: boolean;
}

export interface RankableRecipe {
  title: string;
  ingredients: string[];
  readyInMinutes?: number;
  handsOnMinutes?: number;
  diets?: string[];
  dishTypes?: string[];
  cuisines?: string[];
}

/**
 * The day as the cook's schedule has it. A cook who never opened the schedule
 * gets the default one, exactly as the planner always has — vegetarian
 * Wednesday, fish Friday, 30 minutes on weeknights — so the email and
 * Régénérer no longer disagree with it about an untouched account.
 */
export function planSlotFor(date: string, schedule?: MealSchedulePreferences | null): PlanSlot {
  const dayOfWeek = parseIsoDate(date).getDay();
  const day = schedule?.days?.[dayOfWeek] ?? DEFAULT_MEAL_SCHEDULE.days[dayOfWeek];
  return {
    date,
    dayOfWeek,
    kind: (day?.mealType ?? 'any') as SlotKind,
    maxCookTime: day?.maxCookTimeMinutes ?? null,
    allowPrepAhead: day?.allowPrepAhead ?? true,
  };
}

const lower = (r: RankableRecipe) => [r.title, ...r.ingredients].join(' ').toLowerCase();

export function isSlowCookerRecipe(recipe: RankableRecipe): boolean {
  return /slow.?cooker|crockpot|crock.?pot|mijoteuse|instant.?pot/i.test(lower(recipe));
}

export function isPrepAheadRecipe(recipe: RankableRecipe): boolean {
  return /\b(prep.?ahead|make.?ahead|overnight|marinate|batch.?cook|meal.?prep|prepare.?in.?advance)\b/i.test(lower(recipe));
}

/** Does the recipe answer what the day asks for? `any` takes everything. */
export function fitsSlotKind(recipe: RankableRecipe, kind: SlotKind): boolean {
  switch (kind) {
    case 'vegetarian': return isVegetarianRecipe(recipe);
    case 'fish': return isFishRecipe(recipe);
    case 'meat': return ['chicken', 'beef', 'pork', 'lamb', 'turkey'].includes(detectProtein(recipe) ?? '');
    case 'pasta': return dishFormatOf(recipe.title) === 'pasta';
    default: return true;
  }
}

const hasHandsOn = (recipe: { handsOnMinutes?: number }): boolean => Number(recipe.handsOnMinutes) > 0;

/**
 * The minutes a busy evening is judged on (#850): Préparation, the cook's own
 * time, when the recipe has one, and otherwise Prêt en, the only number it has.
 * A day that refuses to start early (`allowPrepAhead: false`) reads Prêt en: the
 * whole dish has to fit in the evening. 0 means unknown.
 */
export function busyEveningMinutes(
  recipe: { handsOnMinutes?: number; readyInMinutes?: number },
  allowPrepAhead = true,
): number {
  if (allowPrepAhead && hasHandsOn(recipe)) return Number(recipe.handsOnMinutes);
  return Number(recipe.readyInMinutes) || 0;
}

/**
 * A slow-cooker or make-ahead dish, going by its words, may ignore the cap. Only
 * a recipe with no Préparation is read that way: a known Préparation already
 * says how much of the evening the dish takes.
 */
export function escapesTimeCap(recipe: RankableRecipe, allowPrepAhead: boolean | undefined): boolean {
  return !!allowPrepAhead && !hasHandsOn(recipe) && (isSlowCookerRecipe(recipe) || isPrepAheadRecipe(recipe));
}

/**
 * Within the day's time cap. `strict` asks for a time we know: 0 means "we
 * never read it", not "instant", so an unknown time is only accepted once no
 * dish known to fit is left.
 */
export function fitsSlotTime(recipe: RankableRecipe, slot: PlanSlot, strict = false): boolean {
  if (slot.maxCookTime === null) return true;
  const minutes = busyEveningMinutes(recipe, slot.allowPrepAhead);
  if (minutes <= 0) return !strict;
  if (minutes <= slot.maxCookTime) return true;
  return escapesTimeCap(recipe, slot.allowPrepAhead);
}

/** Dinners in one week that may share a main protein. */
export const MAX_SAME_PROTEIN = 2;
/** Fish and seafood dinners in one week, unless the schedule sets more fish days. */
export const MAX_FISH_DINNERS = 2;

const traitCache = new WeakMap<object, { protein: string | null; fish: boolean }>();

/** Main protein and fish-ness, once per recipe object — every slot asks again. */
function traitsOf(recipe: RankableRecipe): { protein: string | null; fish: boolean } {
  let hit = traitCache.get(recipe);
  if (!hit) {
    hit = {
      protein: mainProteinOf(recipe.title, recipe.ingredients),
      fish: classifyDietKind(recipe) === 'fish',
    };
    traitCache.set(recipe, hit);
  }
  return hit;
}

/**
 * What the week holds so far. A third chicken or a third fish dinner is not
 * forbidden — a small library may have nothing else — but it is only served
 * when nothing that keeps the week balanced fits the slot.
 *
 * The fish days the cook scheduled are reserved, whatever order the slots are
 * filled in: two fish dinners on Monday and Tuesday must not leave Friday's
 * fish day as the third.
 */
export class WeekBalance {
  private proteins = new Map<string, number>();
  private fish = 0;
  private fishDaysLeft: number;
  private readonly fishCap: number;

  constructor(fishDays = 0) {
    this.fishCap = Math.max(MAX_FISH_DINNERS, fishDays);
    this.fishDaysLeft = fishDays;
  }

  add(recipe: RankableRecipe, slot?: Pick<PlanSlot, 'kind'>): void {
    const { protein, fish } = traitsOf(recipe);
    if (protein) this.proteins.set(protein, (this.proteins.get(protein) ?? 0) + 1);
    if (fish) this.fish++;
    if (slot?.kind === 'fish' && this.fishDaysLeft > 0) this.fishDaysLeft--;
  }

  fits(recipe: RankableRecipe, slot?: Pick<PlanSlot, 'kind'>): boolean {
    const { protein, fish } = traitsOf(recipe);
    // A fish day the cook asked for is served even when fish is already the
    // week's most frequent protein.
    const askedFor = fish && slot?.kind === 'fish';
    if (!askedFor && protein && (this.proteins.get(protein) ?? 0) >= MAX_SAME_PROTEIN) return false;
    if (!fish) return true;
    // A fish day spends its own reservation; any other day may only use what
    // the reservations leave.
    const reserved = slot?.kind === 'fish' ? this.fishDaysLeft - 1 : this.fishDaysLeft;
    return this.fish + Math.max(0, reserved) < this.fishCap;
  }
}

export interface NarrowOptions {
  /**
   * Also hold the slot to the day's kind. Off for an engine that already
   * enforces kinds its own way (the planner's slot types, Régénérer's
   * per-cell diets), on for one that has no other notion of them (the email).
   */
  kind?: boolean;
  /**
   * The fewest candidates a tier must leave before it is accepted. The
   * generators need one dish per slot (the default); a suggestion list needs
   * a handful, or a tight tier would leave it a single card. When no tier
   * reaches it, the strictest tier that leaves anything wins.
   */
  minKeep?: number;
}

/**
 * The candidates that honour the day and the week, in their original order.
 * The rules give way loosest first — a known cook time, then the balance,
 * then the time cap, then the kind — and only when honouring them would leave
 * the slot with nothing at all.
 */
export function narrowForSlot<T>(
  candidates: T[],
  recipeOf: (candidate: T) => RankableRecipe,
  slot: PlanSlot,
  balance: WeekBalance,
  opts: NarrowOptions = {},
): T[] {
  const kind = (c: T) => !opts.kind || fitsSlotKind(recipeOf(c), slot.kind);
  const knownTime = (c: T) => fitsSlotTime(recipeOf(c), slot, true);
  const time = (c: T) => fitsSlotTime(recipeOf(c), slot);
  const balanced = (c: T) => balance.fits(recipeOf(c), slot);
  const tiers: Array<(c: T) => boolean> = [
    (c) => kind(c) && knownTime(c) && balanced(c),
    (c) => kind(c) && time(c) && balanced(c),
    (c) => kind(c) && time(c),
    kind,
  ];
  const minKeep = Math.max(1, opts.minKeep ?? 1);
  let strictestNonEmpty: T[] | null = null;
  for (const keep of tiers) {
    const kept = candidates.filter(keep);
    if (kept.length >= minKeep) return kept;
    if (!strictestNonEmpty && kept.length > 0) strictestNonEmpty = kept;
  }
  return strictestNonEmpty ?? candidates;
}

/** A dinner already on the week, as the balance reads it. */
export interface PlacedDinner {
  date: string;
  recipe: RankableRecipe;
}

/** The day a suggestion list fills, and what the rest of its week holds. */
export interface SlotFit {
  slot: PlanSlot;
  balance: WeekBalance;
}

/**
 * What a swap must answer: the cook's schedule for `date` and the balance of
 * its Monday week, seeded with the dinners already there. Pass every other
 * dinner of that week — not the one being replaced, which is leaving. A
 * suggestion then obeys the same day and week rules as the week it lands in.
 */
export function slotFitFor(
  date: string,
  schedule: MealSchedulePreferences | null | undefined,
  otherDinners: PlacedDinner[],
): SlotFit {
  const monday = weekStartOf(date);
  const week = Array.from({ length: 7 }, (_, i) => planSlotFor(addDays(monday, i), schedule));
  const balance = new WeekBalance(fishDaysIn(week));
  for (const dinner of otherDinners) balance.add(dinner.recipe, planSlotFor(dinner.date, schedule));
  return { slot: planSlotFor(date, schedule), balance };
}

/**
 * A score at or below this sinks a recipe out of contention — the taste
 * axes use -1000 for the opposite heat and for a long dish on a quick
 * weeknight, and the planner drops everything under -500 the same way.
 */
export const DISQUALIFYING_SCORE = -500;

/** How many of these slots are fish days — the fish cap never undercuts them. */
export function fishDaysIn(slots: Array<Pick<PlanSlot, 'kind'>>): number {
  return slots.filter((s) => s.kind === 'fish').length;
}
